import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, Button, Alert, Row, Col, Image, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { getImageFrameStyle, getImageTagStyle } from '../constants/imageDisplay';
import CoverTextSettingsControls from '../components/CoverTextSettingsControls';

const TOTAL_REVIEW_PAGES = 14;
const MIN_GALLERY_PAGES = 13;

function AdminPagesReviewScreen() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const [job, setJob] = useState(null);
    const [images, setImages] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [busyPage, setBusyPage] = useState(null);

    const authConfig = () => ({ headers: { Authorization: `Bearer ${userInfo.token}` } });

    const loadJob = () => axios.get(`/api/genai/admin/jobs/${jobId}/`, authConfig()).then(res => setJob(res.data));
    const loadImages = () => axios.get(`/api/genai/admin/jobs/${jobId}/images/`, authConfig()).then(res => setImages(res.data));

    useEffect(() => {
        if (!userInfo || !userInfo.isAdmin) {
            navigate('/login');
            return;
        }
        setLoading(true);
        Promise.all([loadJob(), loadImages()])
            .catch(err => setError(err.response?.data?.error || err.message))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, navigate, userInfo]);

    // Redirect once assembly starts/finishes
    useEffect(() => {
        if (!job) return;
        if (job.status === 'completed' || ['I_assemble', 'J_approve_final'].includes(job.current_step)) {
            navigate(`/admin/genai/jobs/${jobId}/final-review`);
        }
    }, [job, jobId, navigate]);

    // Poll while images are still generating/incomplete
    useEffect(() => {
        if (!job) return;
        const reviewPages = images ? Object.keys(images).filter(k => Number(k) >= 0 && Number(k) <= 13) : [];
        const isGenerating = job.status === 'images_generating' || job.status === 'images_qa';
        const incomplete = job.status === 'awaiting_approval' && reviewPages.length < MIN_GALLERY_PAGES;
        if (!isGenerating && !incomplete) return;
        const interval = setInterval(() => { loadJob(); loadImages(); }, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.status, images]);

    const handleApproveAll = () => {
        setActionLoading(true);
        setActionError(null);
        axios.post(`/api/genai/jobs/${jobId}/approve-pages/`, {}, authConfig())
            .then(() => navigate(`/admin/genai/jobs/${jobId}/final-review`))
            .catch(err => setActionError(err.response?.data?.error || err.message))
            .finally(() => setActionLoading(false));
    };

    const handleRegeneratePage = (pageNum) => {
        const feedback = window.prompt(`What should change about ${pageNum === '0' ? 'the front cover' : pageNum === '13' ? 'the back cover' : `page ${pageNum}`}?`);
        if (!feedback || !feedback.trim()) return;
        setBusyPage(pageNum);
        setActionError(null);
        const endpoint = pageNum === '0'
            ? `/api/genai/jobs/${jobId}/regenerate-cover/`
            : `/api/genai/jobs/${jobId}/regenerate-page/`;
        const payload = pageNum === '0' ? { feedback } : { page: pageNum, feedback };
        axios.post(endpoint, payload, authConfig())
            .then(() => { loadJob(); loadImages(); })
            .catch(err => setActionError(err.response?.data?.error || err.message))
            .finally(() => setBusyPage(null));
    };

    if (loading) return <Loader />;
    if (error) return <Message variant="danger">{error}</Message>;
    if (!job) return null;

    const reviewPages = images
        ? Object.entries(images)
            .filter(([pageNum]) => Number(pageNum) >= 0 && Number(pageNum) <= 13)
            .sort(([a], [b]) => Number(a) - Number(b))
        : [];
    const allReady = reviewPages.length === TOTAL_REVIEW_PAGES;
    const galleryReady = job.status === 'awaiting_approval' && reviewPages.length >= MIN_GALLERY_PAGES;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>Staff QA — Pages</h1>
                <Link to={`/admin/genai/jobs/${jobId}`}>Back to job detail</Link>
            </div>

            <Alert variant="info">Customer chose "TaleTots reviews for me". Job {jobId}</Alert>
            {actionError && <Message variant="danger">{actionError}</Message>}

            <CoverTextSettingsControls
                job={job}
                token={userInfo.token}
                onSaved={(updatedJob) => setJob(updatedJob)}
            />

            {!galleryReady ? (
                <div className="text-center py-5 text-muted"><Loader /><p className="mt-3">Pages are still being generated…</p></div>
            ) : (
                <>
                    <Row className="mb-4">
                        {reviewPages.map(([pageNum, image]) => (
                            <Col key={pageNum} md={6} lg={4} className="mb-4">
                                <Card className="h-100 shadow-sm">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <strong>{pageNum === '0' ? 'Front Cover' : pageNum === '13' ? 'Back Cover' : `Page ${pageNum}`}</strong>
                                        {busyPage === pageNum && <Badge bg="warning" text="dark">Regenerating…</Badge>}
                                    </Card.Header>
                                    <Card.Body className="p-2">
                                        <div style={getImageFrameStyle(image, pageNum, { maxHeightCover: 260, maxHeightStory: 260 })}>
                                            <Image
                                                src={image.url || image.image_url}
                                                alt={`Page ${pageNum}`}
                                                fluid
                                                className="border rounded"
                                                style={getImageTagStyle()}
                                            />
                                        </div>
                                        <div className="d-flex gap-2 justify-content-center mt-2">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleRegeneratePage(pageNum)}
                                                disabled={busyPage !== null}
                                            >
                                                Request changes
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <Card className="mb-4 p-3 bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Approve all pages on the customer's behalf?</h5>
                            <Button variant="success" size="lg" onClick={handleApproveAll} disabled={actionLoading || !allReady || busyPage !== null}>
                                {actionLoading ? 'Approving…' : 'Approve All'}
                            </Button>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}

export default AdminPagesReviewScreen;
